import { useState, useEffect } from 'react';
import api from '@/api';
import { Loader2, Wrench, Upload, FileText, Store, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { STATUS_COLORS } from '@/constants/statusColors';

const PRIORITY_STRIP = { emergency:'strip-emergency', high:'strip-high', medium:'strip-medium', low:'strip-low' };

export default function VendorOrders() {
  const [me, setMe] = useState(null);
  const [shop, setShop] = useState(null);
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estimate modal
  const [estimateTarget, setEstimateTarget] = useState(null);
  const [estimate, setEstimate] = useState('');
  const [updating, setUpdating] = useState(false);

  // Invoice upload modal
  const [uploadTarget, setUploadTarget] = useState(null);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [uploadNote, setUploadNote] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => { boot(); }, []);

  const boot = async () => {
    setLoading(true);
    try {
      const meRes = await api.get('/auth/me');
      setMe(meRes.data);
      // The vendors list endpoint is admin-accessible. Vendor can look up their
      // own shop via a single GET — if it 403s we just show the linkage warning.
      if (meRes.data.vendor_id) {
        try {
          const v = await api.get(`/vendors/${meRes.data.vendor_id}`);
          setShop(v.data);
        } catch { setShop(null); }
      }
      const r = await api.get('/repairs', { params: { limit: 50 } });
      setRepairs((r.data.items || []).filter(x => !['cancelled'].includes(x.status)));
    } catch {
      toast.error('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  };

  const submitEstimate = async (e) => {
    e.preventDefault();
    if (!estimate || !estimateTarget) return;
    setUpdating(true);
    try {
      await api.put(`/repairs/${estimateTarget.id}`, {
        estimate_amount: Number(estimate),
        status: 'estimate_received',
        update_note: `Vendor estimate submitted: $${Number(estimate).toLocaleString()}`,
      });
      toast.success('Estimate submitted!');
      setEstimateTarget(null);
      setEstimate('');
      boot();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit estimate');
    } finally {
      setUpdating(false);
    }
  };

  const markInProgress = async (repair) => {
    try {
      await api.put(`/repairs/${repair.id}`, { status: 'repair_in_progress', update_note: 'Work started by vendor' });
      toast.success('Marked as in progress');
      boot();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const handleInvoiceUpload = async (e) => {
    e.preventDefault();
    if (!invoiceFile || !uploadTarget) return;
    if (!invoiceFile.type.includes('pdf')) {
      toast.error('Only PDF invoices are accepted');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', invoiceFile);
      if (invoiceNumber) fd.append('invoice_number', invoiceNumber);
      if (invoiceDate) fd.append('invoice_date', invoiceDate);
      if (uploadNote) fd.append('notes', uploadNote);
      await api.post(`/repairs/${uploadTarget.id}/vendor-upload-invoice`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Invoice sent to ops for review');
      setUploadTarget(null);
      setInvoiceFile(null);
      setInvoiceNumber('');
      setInvoiceDate('');
      setUploadNote('');
      boot();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="vendor-orders-page">
      {/* Shop banner */}
      <div className="pb-4 border-b border-[#2D3F5E]/60">
        <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-1">// VENDOR PORTAL</p>
        <h1 className="text-white font-['Manrope'] font-extrabold text-2xl flex items-center gap-2">
          <Store size={22} className="text-[#3B82F6]" />
          {shop?.name || (me?.vendor_id ? 'Your Shop' : 'Vendor Portal')}
        </h1>
        {shop && (
          <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] text-sm mt-1">
            {repairs.length} work order{repairs.length === 1 ? '' : 's'} assigned to your shop
            {shop.city && ` · ${shop.city}, ${shop.us_state || ''}`}
          </p>
        )}
        {!me?.vendor_id && !loading && (
          <div className="mt-3 p-3 rounded-sm border flex items-start gap-2"
            style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.35)' }}>
            <Info size={14} className="mt-0.5" style={{ color: 'var(--warning)' }} />
            <div className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--warning)' }}>
              Your account isn't linked to a shop yet. Ask the ops admin to attach a vendor on your profile.
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="text-[#3B82F6] animate-spin" size={24} /></div>
      ) : repairs.length === 0 ? (
        <div className="py-16 text-center card-industrial">
          <Wrench size={32} className="mx-auto mb-3 text-[#2D3F5E]" />
          <p className="text-[#5C6A7A] font-['IBM_Plex_Sans']">No work orders assigned to your shop yet</p>
          <p className="text-[10px] mt-2 font-['JetBrains_Mono'] text-[#5C6A7A]">
            When ops assigns you a repair, it will appear here.
          </p>
        </div>
      ) : (
        <div className="card-industrial overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#2D3F5E]">
                {['', 'UNIT', 'ISSUE', 'LOCATION', 'STATUS', 'PRIORITY', 'ACTIONS'].map((h, i) => (
                  <th key={`hdr-${i}`} className={`text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] ${h === '' ? 'w-1' : ''}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {repairs.map((r) => (
                <tr key={r.id} className={`border-b border-[#2D3F5E]/30 hover:bg-[#1A2332] transition-colors ${PRIORITY_STRIP[r.priority] || 'strip-low'}`}
                  data-testid={`vendor-order-${r.id}`}>
                  <td className="w-1 p-0" />
                  <td className="px-4 py-3 font-['JetBrains_Mono'] text-xs text-[#3B82F6] font-bold">{r.truck_unit_number || '—'}</td>
                  <td className="px-4 py-3 text-sm text-white font-['IBM_Plex_Sans'] max-w-xs truncate" title={r.issue_description}>
                    {r.issue_description}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#B8C0CC] font-['IBM_Plex_Sans']">{r.location}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${STATUS_COLORS[r.status] || STATUS_COLORS.open}`}>
                      {r.status?.replace(/_/g,' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[9px] font-bold uppercase font-['JetBrains_Mono'] ${r.priority==='emergency'?'text-red-400':r.priority==='high'?'text-orange-400':r.priority==='medium'?'text-[#3B82F6]':'text-[#5C6A7A]'}`}>
                      {r.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {!['estimate_received','approved','repair_in_progress','completed','invoice_created'].includes(r.status) && (
                        <button onClick={() => { setEstimateTarget(r); setEstimate(''); }}
                          className="px-2.5 py-1 text-[9px] font-bold uppercase tracking-wide text-[#3B82F6] border border-[#3B82F6]/40 rounded-sm hover:bg-[#3B82F6]/10 transition-colors font-['JetBrains_Mono']"
                          data-testid={`submit-estimate-${r.id}`}>
                          ESTIMATE
                        </button>
                      )}
                      {r.status === 'approved' && (
                        <button onClick={() => markInProgress(r)}
                          className="px-2.5 py-1 text-[9px] font-bold uppercase tracking-wide text-[#F59E0B] border border-[#F59E0B]/40 rounded-sm hover:bg-[#F59E0B]/10 transition-colors font-['JetBrains_Mono']"
                          data-testid={`start-work-${r.id}`}>
                          START WORK
                        </button>
                      )}
                      <button onClick={() => { setUploadTarget(r); setInvoiceFile(null); setInvoiceNumber(''); setInvoiceDate(''); setUploadNote(''); }}
                        className="px-2.5 py-1 text-[9px] font-bold uppercase tracking-wide text-[#57EB31] border border-[#57EB31]/40 rounded-sm hover:bg-[#57EB31]/10 transition-colors font-['JetBrains_Mono'] inline-flex items-center gap-1"
                        title="Send your invoice PDF to ops for AI extraction"
                        data-testid={`upload-invoice-${r.id}`}>
                        <Upload size={10} /> INVOICE
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ESTIMATE MODAL */}
      <Dialog open={!!estimateTarget} onOpenChange={() => setEstimateTarget(null)}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E]">
          <DialogHeader>
            <DialogTitle className="text-white font-['Manrope']">Submit Estimate</DialogTitle>
          </DialogHeader>
          {estimateTarget && (
            <div className="mt-2 space-y-4">
              <div className="p-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm">
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">WORK ORDER</p>
                <p className="text-sm text-white font-['IBM_Plex_Sans']">{estimateTarget.issue_description}</p>
                <p className="text-xs text-[#5C6A7A] font-['JetBrains_Mono'] mt-1">{estimateTarget.truck_unit_number} · {estimateTarget.location}</p>
              </div>
              <form onSubmit={submitEstimate} className="space-y-3">
                <div>
                  <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Estimate Amount ($) *</label>
                  <input type="number" step="0.01" required value={estimate} onChange={e => setEstimate(e.target.value)}
                    className="w-full h-11 px-4 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-lg font-bold focus:outline-none focus:border-[#3B82F6] transition-colors"
                    placeholder="0.00" data-testid="estimate-amount" />
                </div>
                <button type="submit" disabled={updating} data-testid="estimate-submit"
                  className="w-full h-10 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#2563EB] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {updating && <Loader2 size={14} className="animate-spin" />} Submit Estimate
                </button>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* INVOICE UPLOAD MODAL */}
      <Dialog open={!!uploadTarget} onOpenChange={() => setUploadTarget(null)}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-['Manrope'] flex items-center gap-2">
              <FileText size={18} /> Send Invoice to Ops
            </DialogTitle>
          </DialogHeader>
          {uploadTarget && (
            <div className="mt-2 space-y-4">
              <div className="p-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm">
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">FOR WORK ORDER</p>
                <p className="text-sm text-white font-['IBM_Plex_Sans']">{uploadTarget.issue_description}</p>
                <p className="text-xs text-[#5C6A7A] font-['JetBrains_Mono'] mt-1">{uploadTarget.truck_unit_number} · {uploadTarget.location}</p>
              </div>
              <form onSubmit={handleInvoiceUpload} className="space-y-3">
                <div>
                  <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Invoice PDF *</label>
                  <input type="file" accept="application/pdf,.pdf" required
                    onChange={e => setInvoiceFile(e.target.files?.[0] || null)}
                    data-testid="invoice-file-input"
                    className="w-full text-xs text-[#B8C0CC] file:mr-3 file:px-3 file:py-2 file:rounded-sm file:border-0 file:bg-[#3B82F6] file:text-white file:font-bold file:uppercase file:text-[10px] file:cursor-pointer" />
                  {invoiceFile && (
                    <p className="text-[10px] mt-1.5 font-['JetBrains_Mono']" style={{ color: 'var(--success)' }}>
                      ✓ {invoiceFile.name} ({Math.round(invoiceFile.size / 1024)} KB)
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Invoice #</label>
                    <input type="text" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)}
                      placeholder="INV-12345" data-testid="invoice-number"
                      className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-sm focus:outline-none focus:border-[#3B82F6]" />
                  </div>
                  <div>
                    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Invoice Date</label>
                    <input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)}
                      data-testid="invoice-date"
                      className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-sm focus:outline-none focus:border-[#3B82F6]" />
                  </div>
                </div>
                <div>
                  <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Note to Ops (optional)</label>
                  <textarea value={uploadNote} onChange={e => setUploadNote(e.target.value)} rows={2}
                    placeholder="e.g. used OEM parts; ETA pickup tomorrow"
                    data-testid="invoice-note"
                    className="w-full px-3 py-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6] resize-none" />
                </div>
                <p className="text-[10px] font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>
                  Ops will receive your PDF and either run AI extraction or re-edit the line items before billing the customer.
                </p>
                <button type="submit" disabled={uploading || !invoiceFile} data-testid="invoice-upload-submit"
                  className="w-full h-10 bg-[#57EB31] text-[#0A0F1A] font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#46c027] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {uploading ? 'Sending…' : 'Send Invoice'}
                </button>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
